import React, {ReactElement, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import useResizeObserver from 'use-resize-observer'
import {Padding} from '@components/common/base/design-system/types'
import {Icon} from '@components/common/base/icon'
import {globals} from '@extensions/globals-base'

interface CarouselProps {
  children: ReactElement[]
  px?: Padding
  py?: Padding
  scrollMultipler?: number
  onItemClick?: () => void
}

const TwContainer = tw.div`
  relative
`

type TwCarouselProps = {
  $px?: Padding
  $py?: Padding
}

const TwCarousel = tw.div<TwCarouselProps>`
  flex
  gap-8
  overflow-x-auto
  no-scrollbar
  scroll-smooth

  ${p => p.$px && 'px-16'}
  ${p => p.$py && 'py-16'}
`

const TwCarouselItem = tw.div`
  shrink-0
`

type TwNavigationProps = {
  $direction: 'left' | 'right'
}

const TwNavigation = tw.div<TwNavigationProps>`
  group
  absolute
  top-0
  bottom-0
  z-10
  ${p => (p.$direction === 'left' ? 'left-0' : 'right-0')}

  flex
  items-center
  shrink-0
  cursor-pointer
  w-[64px]
`

const TwArrow = tw.div<TwNavigationProps>`
  flex
  justify-center
  items-center
  shrink-0

    w-[32px]
  h-[32px]
  rounded-8

  shadow-md
  shadow-black

  group-hover:scale-[1.13]
  group-active:scale-[1.05]

  ${p =>
    p.$direction === 'left'
      ? 'group-hover:translate-x-[2px]'
      : 'group-hover:translate-x-[-2px]'}
  

  transition-transform
  duration-250
  ease-out

  mx-16
  bg-[rgba(18,27,46,0.5)]
  backdrop-blur
`

export const Carousel: React.FC<CarouselProps> = ({
  px,
  py,
  scrollMultipler = 1,
  children
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const scrollableRef = useRef<HTMLDivElement>(null)

  const updateScrollState = () => {
    if (scrollableRef.current) {
      const {scrollLeft, scrollWidth, clientWidth} = scrollableRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth)
    }
  }

  const scroll = (direction: 'left' | 'right') => {
    if (scrollableRef.current) {
      const scrollAmount =
        (scrollableRef.current.clientWidth / children.length) * scrollMultipler

      requestAnimationFrame(() => {
        scrollableRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        })
      })
    }
  }

  useEffect(() => {
    updateScrollState()
  }, [])

  useResizeObserver({
    ref: scrollableRef,
    onResize: updateScrollState
  })

  // TODO: when globals.theme changes, this not re-renders automatically
  // also need avoid hardcoding values like that, so we can use theme colors instead
  // TODO: var(--color-primary-background-primary) should be used here but as RGBA()
  const gradientColor =
    globals.theme === 'light' ? '255, 255, 255' : '37, 43, 53'

  return (
    <>
      <TwContainer>
        <TwNavigation
          $direction="left"
          onClick={() => scroll('left')}
          style={{
            opacity: canScrollLeft ? 1 : 0,
            pointerEvents: canScrollLeft ? 'auto' : 'none',
            background: `linear-gradient(270deg, rgba(${gradientColor}, 0.00) 0%, rgba(${gradientColor}, 0.85) 100%)`
          }}
        >
          <TwArrow $direction="left">
            <Icon icon="IconArrowLeft" color="icon-invert" size={16} />
          </TwArrow>
        </TwNavigation>
        <TwCarousel
          ref={scrollableRef}
          $px={px}
          $py={py}
          onScroll={updateScrollState}
        >
          {children.map(child => (
            <TwCarouselItem key={`carousel-item-${child.key}`}>
              {child}
            </TwCarouselItem>
          ))}
        </TwCarousel>
        <TwNavigation
          $direction="right"
          onClick={() => scroll('right')}
          style={{
            opacity: canScrollRight ? 1 : 0,
            pointerEvents: canScrollRight ? 'auto' : 'none',
            background: `linear-gradient(90deg, rgba(${gradientColor}, 0.00) 0%, rgba(${gradientColor}, 0.90) 100%)`
          }}
        >
          <TwArrow $direction="right">
            <Icon icon="IconArrowRight" color="icon-invert" size={16} />
          </TwArrow>
        </TwNavigation>
      </TwContainer>
    </>
  )
}
